import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, RefreshCw, ScanLine, Maximize2, Users } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { sessionYearsLabel } from "@/lib/school";

const REFRESH_MS = 15000;

export default function AttendanceQR() {
  const { id } = useParams();
  const [token, setToken] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [remaining, setRemaining] = useState(15);
  const [fullscreen, setFullscreen] = useState(false);
  const [error, setError] = useState(null);
  const [lastCheckins, setLastCheckins] = useState([]);

  const { data: session } = useQuery({
    queryKey: ["attendance-session", id],
    queryFn: async () => (await api.get(`/attendance/sessions/${id}`)).data,
    refetchInterval: 5000,
  });

  const fetchToken = useCallback(async () => {
    try {
      const { data } = await api.post(`/attendance/sessions/${id}/qr-token`);
      setToken(data.token);
      setExpiresAt(new Date(data.expires_at));
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore generazione QR");
    }
  }, [id]);

  useEffect(() => {
    fetchToken();
    const iv = setInterval(fetchToken, REFRESH_MS);
    return () => clearInterval(iv);
  }, [fetchToken]);

  useEffect(() => {
    if (!expiresAt) return;
    const t = setInterval(() => {
      const r = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
      setRemaining(r);
    }, 500);
    return () => clearInterval(t);
  }, [expiresAt]);

  // Detect newly arrived check-ins by diffing session records
  useEffect(() => {
    if (!session?.records) return;
    const withCheckin = session.records
      .filter((r) => (r.note || "").includes("check-in QR"))
      .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    setLastCheckins(withCheckin.slice(0, 8));
  }, [session]);

  if (!session) return <div className="p-10 text-stone-500 text-sm">Caricamento…</div>;

  const checkinUrl = token
    ? `${window.location.origin}/checkin?token=${encodeURIComponent(token)}`
    : "";

  return (
    <div className={fullscreen ? "fixed inset-0 z-50 bg-[#1C352D] text-white p-8 md:p-14 overflow-auto" : "p-8 md:p-10 max-w-[1400px] mx-auto"}>
      <div className={fullscreen ? "flex items-center justify-between mb-6" : ""}>
        {!fullscreen && (
          <Link to={`/scuola/presenze/${id}`} className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4">
            <ArrowLeft size={13}/> Torna alla sessione
          </Link>
        )}
        {fullscreen && (
          <div className="flex items-center gap-3 text-stone-100">
            <ScanLine size={20}/>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">RDM ClassRoom</div>
              <div className="text-sm font-medium">{session.title}</div>
            </div>
          </div>
        )}
        {fullscreen && (
          <Button variant="secondary" size="sm" className="bg-white/10 hover:bg-white/20 text-white border-0" onClick={() => setFullscreen(false)}>
            Chiudi
          </Button>
        )}
      </div>

      {!fullscreen && (
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">
              {fmtDate(session.date)}{sessionYearsLabel(session) ? ` · ${sessionYearsLabel(session)}` : ""}
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-[#1C352D]">{session.title}</h1>
            <div className="text-sm text-stone-600 mt-1">
              Mostra questo QR agli studenti: si aggiorna ogni 15 secondi.
            </div>
          </div>
          <Button data-testid="qr-fullscreen-btn" onClick={() => setFullscreen(true)} className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]">
            <Maximize2 size={14} className="mr-1.5"/> Modalità presentazione
          </Button>
        </div>
      )}

      <div className={fullscreen ? "grid grid-cols-1 lg:grid-cols-5 gap-8 items-center" : "grid grid-cols-1 lg:grid-cols-5 gap-6"}>
        <Card className={`${fullscreen ? "lg:col-span-3 bg-white border-0 shadow-xl" : "lg:col-span-3 border border-stone-200 shadow-none"} rounded-2xl p-8 text-center`}>
          {error ? (
            <div className="text-red-700 py-16">{error}</div>
          ) : token ? (
            <div className="flex flex-col items-center gap-6">
              <div className="bg-white p-4 rounded-xl border border-stone-100">
                <QRCodeSVG
                  data-testid="attendance-qr"
                  value={checkinUrl}
                  size={fullscreen ? 480 : 320}
                  bgColor="#ffffff"
                  fgColor="#1C352D"
                  level="M"
                  includeMargin={false}
                />
              </div>
              <div className="flex items-center gap-3 text-sm text-stone-600">
                <RefreshCw size={14} className={remaining <= 3 ? "animate-spin text-[#E2725B]" : ""}/>
                Prossimo aggiornamento in <span className="mono font-semibold text-[#1C352D]">{remaining}s</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-stone-400">
                Scansiona con la fotocamera → inserisci matricola
              </div>
            </div>
          ) : (
            <div className="text-stone-500 py-16">Generazione QR in corso…</div>
          )}
        </Card>

        <Card className={`lg:col-span-2 ${fullscreen ? "bg-white/95 border-0 shadow-xl" : "border border-stone-200 shadow-none"} rounded-2xl p-6`}>
          <div className="text-sm font-medium mb-3 flex items-center gap-2 text-[#1C352D]">
            <Users size={15}/> Ultimi check-in
          </div>
          {lastCheckins.length === 0 ? (
            <div className="text-xs text-stone-500 py-8 text-center">
              Nessun check-in ancora ricevuto. In attesa di scansioni…
            </div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {lastCheckins.map((r) => (
                <li key={r.id} className="py-2.5 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium">{r.student_name}</div>
                    <div className="text-[11px] text-stone-500 mono">{r.matricola}</div>
                  </div>
                  <div className="text-xs text-stone-600 mono">
                    {r.arrival_time || "—"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
